// One reader for every keyboard-wedge scanner listener (Fernando 28.10: the badge scan at the station beeped and
// nothing happened; "can we make it so that it works no matter which keyboard is selected?").
//
// What was wrong: seven listeners collected keystrokes and waited for an Enter. The fixed station scanner sends none
// (the packing inputs work because they run the scan 150 ms after the last character), so a badge read died silently.
// And they read `e.key`: under a RUS layout a scanner's letters arrive as Cyrillic.
//
// The rules here:
//   - a character comes from the PHYSICAL key (`e.code` KeyA…KeyZ, Digit0…9, Numpad0…9) — the same under EST, ENG and
//     RUS — and from `e.key` only for other printable characters (e.g. '-');
//   - a gap of more than `gapMs` since the previous character starts a new code (a person typing is not a scan);
//   - Enter or Tab ends the code at once; otherwise it ends `idleMs` after the last character when at least `minLen`
//     characters arrived in one burst;
//   - one `onCode` per burst, never two (an Enter arriving after the idle fire finds an empty buffer);
//   - a SHIFTED digit is the layout's symbol ("%" starts Colissimo and DPD barcodes), and a code that is the same
//     string twice is one code (1.11, after the first Packeta handover).
// Pure (timers injectable) so scanWedge.test.ts can drive it. -- Claude, 2026-10-28
import { useEffect, useRef } from 'react';

export type ScanEnd = 'enter' | 'tab' | 'idle';
export type KeyLike = { key: string; code?: string; altKey?: boolean; ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean };

export interface WedgeOptions {
  onCode: (code: string, how: ScanEnd) => void;
  minLen?: number;            // characters needed before silence counts as the end (default 4)
  idleMs?: number;            // silence after the last character that ends a code (default 120)
  gapMs?: number;             // a longer pause between two characters = a new code (default 250)
  now?: () => number;
  setTimeout?: (fn: () => void, ms: number) => any;
  clearTimeout?: (id: any) => void;
}

/** The character a key event stands for on a scanner, or '' when it is none (modifiers, arrows, F-keys…). */
export function wedgeChar(e: KeyLike): string {
  if (e.altKey || e.ctrlKey || e.metaKey) return '';
  const c = e.code || '';
  const k = e.key || '';
  if (/^Key[A-Z]$/.test(c)) return c[3];
  // a SHIFTED digit is the layout's symbol, not the digit: Colissimo and DPD barcodes start with "%" (Shift+5),
  // which this reader turned into "5" on 29.10 — FR1450 and a DPD label were "not a label the WMS knows" (1.11)
  if (/^Digit[0-9]$/.test(c)) return e.shiftKey && k.length === 1 && k !== ' ' ? k : c[5];
  if (/^Numpad[0-9]$/.test(c)) return c[6];
  return k.length === 1 && k !== ' ' ? k : '';
}

/** A code that is the same string twice in one burst is one code: a label read twice by one trigger
 *  ("Z3741159288Z3741159288" at the Packeta handover 29.10). Halves of at least 6 characters only. */
export function unDouble(code: string): string {
  const n = code.length;
  if (n < 12 || n % 2) return code;
  const half = code.slice(0, n / 2);
  return half === code.slice(n / 2) ? half : code;
}

/** True when the keystroke belongs to a text field the person is typing in (the global listeners stay out). */
export function typingInField(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || (el as HTMLElement).isContentEditable) return true;
  if (tag !== 'INPUT') return false;
  const type = ((el as HTMLInputElement).type || 'text').toLowerCase();
  return !['checkbox', 'radio', 'button', 'submit', 'range', 'color', 'file'].includes(type);
}

/** A code no WMS listener wants: a QR code's URL, or something far longer than any barcode (Fernando 30.10: product
 *  labels carry a QR next to the barcode, shipping labels too — "some QR codes open like a new tab"). */
export function isForeignCode(code: string): boolean {
  return code.length > 60 || code.includes('://') || /^(https?:|www\.)/i.test(code);
}

export function createWedge(opts: WedgeOptions) {
  const minLen = opts.minLen ?? 4, idleMs = opts.idleMs ?? 120, gapMs = opts.gapMs ?? 250;
  const now = opts.now || (() => Date.now());
  const setT = opts.setTimeout || ((fn: () => void, ms: number) => window.setTimeout(fn, ms));
  const clearT = opts.clearTimeout || ((id: any) => window.clearTimeout(id));
  let buf = '', last = 0, timer: any = null;

  const reset = () => { buf = ''; last = 0; if (timer != null) { clearT(timer); timer = null; } };
  const finish = (how: ScanEnd) => {
    const code = unDouble(buf.trim());
    reset();
    if (code) opts.onCode(code, how);
  };

  return {
    /** Feed a keydown. Returns true when the event was a scanner key (a character or a terminator of a code). */
    onKey(e: KeyLike): boolean {
      if (e.key === 'Enter' || e.key === 'Tab') {
        if (!buf) return false;                       // an Enter/Tab of its own: not ours
        finish(e.key === 'Enter' ? 'enter' : 'tab');
        return true;
      }
      const ch = wedgeChar(e);
      if (!ch) return false;
      const t = now();
      if (last && t - last > gapMs) buf = '';
      buf += ch; last = t;
      if (timer != null) clearT(timer);
      timer = setT(() => { timer = null; if (buf.length >= minLen) finish('idle'); }, idleMs);
      return true;
    },
    dispose: reset,
  };
}

/**
 * Mount the reader on `document`. `handler` gets the code; `capture` = the capture phase + stopPropagation on every
 * scanner key (the identify box: nothing behind it may react to the scan); `skipFields` = leave keystrokes alone while
 * a text field has focus (the global fallbacks behind a scan input).
 */
export function useScanWedge(handler: (code: string, how: ScanEnd) => void,
                             { enabled = true, capture = false, skipFields = false, preventDefault = false }:
                             { enabled?: boolean; capture?: boolean; skipFields?: boolean; preventDefault?: boolean } = {}) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!enabled) return;
    const w = createWedge({ onCode: (c, how) => { if (!isForeignCode(c)) ref.current(c, how); } });
    const onKey = (e: KeyboardEvent) => {
      if (skipFields && typingInField(document.activeElement)) return;
      const mine = w.onKey(e);
      if (mine && capture) e.stopPropagation();
      if (mine && (preventDefault || e.key === 'Tab')) e.preventDefault();
    };
    document.addEventListener('keydown', onKey, capture);
    return () => { document.removeEventListener('keydown', onKey, capture); w.dispose(); };
  }, [enabled, capture, skipFields, preventDefault]);
}
